import {
  TASK_PAGE_ICON_KEYS,
  type ColumnConfigEntry,
  type TaskPageIconKey,
  type TaskPageNavGroup,
} from '@erp/shared-types';
import {
  MAX_TASK_PAGE_TASK_TYPES,
  SidebarSortOrderSchema,
  type CreateTaskPageInput,
  type TaskPageResponse,
  type TaskPageTaskTypeWrite,
  type UpdateTaskPageInput,
} from '@erp/rpc';
import { toNavGroup } from 'src/components/task-pages/pagePlacement';

/**
 * Pure draft state + validation for the Task Pages admin dialog, kept free
 * of React imports so it stays trivially unit-testable (mirrors
 * taskTypeAdmin.ts).
 *
 * `defaultFilters` is deliberately NOT edited here (follow-up seam): updates
 * omit the field so the service leaves any seeded filters unchanged.
 */

/** One task type the page shows, as its own tab (draft form). */
export type TaskPageTaskTypeDraft = {
  taskTypeId: string;
  /** Tab label; blank renders the task type name. */
  label: string;
  /** Admin default queue columns for this tab; null = built-in set. */
  defaultColumns: ColumnConfigEntry[] | null;
};

/** Longest tab label the editor accepts (matches the contract). */
export const MAX_TASK_TYPE_LABEL_LENGTH = 255;

export type TaskPageDraft = {
  /** Null when creating; the page id when editing. */
  id: string | null;
  name: string;
  description: string;
  icon: TaskPageIconKey | null;
  /**
   * Task types in tab order; the first is the page's primary type. Each
   * carries its own tab label and default columns. One entry = no tabs.
   */
  taskTypes: TaskPageTaskTypeDraft[];
  /** Built-in sidebar section; null with a null pageSectionId = "Pages". */
  navGroup: TaskPageNavGroup | null;
  /** Org-created page section; set exclusively with `navGroup`. */
  pageSectionId: string | null;
  /** Kept as the raw input string; validated by {@link parseSortOrder}. */
  sortOrder: string;
  audienceUserGroupIds: string[];
  /** False locks the page's columns to the defaults for every user. */
  allowUserColumnCustomization: boolean;
};

export function emptyTaskPageDraft(): TaskPageDraft {
  return {
    id: null,
    name: '',
    description: '',
    icon: null,
    taskTypes: [],
    navGroup: null,
    pageSectionId: null,
    sortOrder: '0',
    audienceUserGroupIds: [],
    allowUserColumnCustomization: true,
  };
}

function toIconKey(value: string | null): TaskPageIconKey | null {
  return TASK_PAGE_ICON_KEYS.find((key) => key === value) ?? null;
}

function copyColumns(
  columns: ColumnConfigEntry[] | null,
): ColumnConfigEntry[] | null {
  return columns ? columns.map((column) => ({ ...column })) : null;
}

/** The page's task types as drafts (deep-copied so edits never touch cache). */
function draftTaskTypesFromPage(
  page: TaskPageResponse,
): TaskPageTaskTypeDraft[] {
  const entries =
    page.taskTypes.length > 0
      ? page.taskTypes
      : [
          {
            taskTypeId: page.taskTypeId,
            label: null,
            defaultColumns: page.defaultColumns,
          },
        ];
  return entries.map((entry) => ({
    taskTypeId: entry.taskTypeId,
    label: entry.label ?? '',
    defaultColumns: copyColumns(entry.defaultColumns),
  }));
}

export function draftFromTaskPage(page: TaskPageResponse): TaskPageDraft {
  return {
    id: page.id,
    name: page.name,
    description: page.description ?? '',
    icon: toIconKey(page.icon),
    taskTypes: draftTaskTypesFromPage(page),
    navGroup: toNavGroup(page.navGroup),
    pageSectionId: page.pageSectionId,
    sortOrder: String(page.sortOrder),
    audienceUserGroupIds: [...page.audienceUserGroupIds],
    allowUserColumnCustomization: page.allowUserColumnCustomization,
  };
}

/**
 * The sort order the API accepts, or null when the input is not a whole
 * number within the contract's bounds (so the editor blocks save rather than
 * letting the mutation fail server-side).
 */
export function parseSortOrder(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = SidebarSortOrderSchema.safeParse(Number(trimmed));
  return parsed.success ? parsed.data : null;
}

export function isTaskPageDraftValid(draft: TaskPageDraft): boolean {
  return (
    draft.name.trim().length > 0 &&
    areDraftTaskTypesValid(draft.taskTypes) &&
    parseSortOrder(draft.sortOrder) !== null
  );
}

/**
 * At least one and at most {@link MAX_TASK_PAGE_TASK_TYPES} task types, each
 * chosen once, with a label the contract accepts and valid default columns.
 */
export function areDraftTaskTypesValid(
  entries: readonly TaskPageTaskTypeDraft[],
): boolean {
  const ids = entries.map((entry) => entry.taskTypeId);
  return (
    entries.length > 0 &&
    entries.length <= MAX_TASK_PAGE_TASK_TYPES &&
    ids.every((id) => id.length > 0) &&
    new Set(ids).size === ids.length &&
    entries.every(isDraftTaskTypeValid)
  );
}

function isDraftTaskTypeValid(entry: TaskPageTaskTypeDraft): boolean {
  return (
    entry.label.trim().length <= MAX_TASK_TYPE_LABEL_LENGTH &&
    areDefaultColumnsValid(entry.defaultColumns)
  );
}

/** Whether another task type can be added (below the per-page limit). */
export function canAddDraftTaskType(
  entries: readonly TaskPageTaskTypeDraft[],
): boolean {
  return entries.length < MAX_TASK_PAGE_TASK_TYPES;
}

/**
 * Append a task type as a new tab (built-in columns, no label). Choosing one
 * already on the page, or going past the limit, is a no-op.
 */
export function addDraftTaskType(
  entries: TaskPageTaskTypeDraft[],
  taskTypeId: string,
): TaskPageTaskTypeDraft[] {
  if (!taskTypeId || !canAddDraftTaskType(entries)) return entries;
  if (entries.some((entry) => entry.taskTypeId === taskTypeId)) return entries;
  return [...entries, { taskTypeId, label: '', defaultColumns: null }];
}

export function removeDraftTaskType(
  entries: TaskPageTaskTypeDraft[],
  index: number,
): TaskPageTaskTypeDraft[] {
  return entries.filter((_, i) => i !== index);
}

export function updateDraftTaskType(
  entries: TaskPageTaskTypeDraft[],
  index: number,
  updates: Partial<Omit<TaskPageTaskTypeDraft, 'taskTypeId'>>,
): TaskPageTaskTypeDraft[] {
  return entries.map((entry, i) =>
    i === index ? { ...entry, ...updates } : entry,
  );
}

/** Swap a task type with its neighbour; out-of-range moves are no-ops. */
export function moveDraftTaskType(
  entries: TaskPageTaskTypeDraft[],
  index: number,
  delta: -1 | 1,
): TaskPageTaskTypeDraft[] {
  return swapNeighbours(entries, index, delta);
}

/** Task type options not already on the page, for the "add type" picker. */
export function availableTaskTypeOptions<Option extends { id: string }>(
  options: readonly Option[],
  entries: readonly TaskPageTaskTypeDraft[],
): Option[] {
  const chosen = new Set(entries.map((entry) => entry.taskTypeId));
  return options.filter((option) => !chosen.has(option.id));
}

/** The page's task type names in tab order, for the admin table. */
export function taskPageTypeNames(page: TaskPageResponse): string[] {
  return page.taskTypes.length > 0
    ? page.taskTypes.map((entry) => entry.name)
    : [page.taskTypeName];
}

/** Every column needs a header; an empty custom list would blank the queue. */
export function areDefaultColumnsValid(
  columns: ColumnConfigEntry[] | null,
): boolean {
  if (columns === null) return true;
  return (
    columns.length > 0 &&
    columns.every((column) => column.header.trim().length > 0)
  );
}

/** Swap a column with its neighbour; out-of-range moves are no-ops. */
export function moveDefaultColumn(
  columns: ColumnConfigEntry[],
  index: number,
  delta: -1 | 1,
): ColumnConfigEntry[] {
  return swapNeighbours(columns, index, delta);
}

function swapNeighbours<Item>(
  items: Item[],
  index: number,
  delta: -1 | 1,
): Item[] {
  const target = index + delta;
  const current = items[index];
  const neighbour = items[target];
  if (current === undefined || neighbour === undefined) return items;
  const next = [...items];
  next[index] = neighbour;
  next[target] = current;
  return next;
}

export function updateDefaultColumn(
  columns: ColumnConfigEntry[],
  id: string,
  updates: Partial<Omit<ColumnConfigEntry, 'id'>>,
): ColumnConfigEntry[] {
  return columns.map((column) =>
    column.id === id ? { ...column, ...updates } : column,
  );
}

export function isTaskPageDraftDirty(
  draft: TaskPageDraft,
  initial: TaskPageDraft,
): boolean {
  return JSON.stringify(draft) !== JSON.stringify(initial);
}

function trimmedColumns(
  columns: ColumnConfigEntry[] | null,
): ColumnConfigEntry[] | null {
  return (
    columns?.map((column) => ({ ...column, header: column.header.trim() })) ??
    null
  );
}

function toTaskTypeWrite(entry: TaskPageTaskTypeDraft): TaskPageTaskTypeWrite {
  return {
    taskTypeId: entry.taskTypeId,
    label: entry.label.trim() || null,
    defaultColumns: trimmedColumns(entry.defaultColumns),
  };
}

/**
 * The create payload. `taskTypes` carries every tab; `taskTypeId` and
 * `defaultColumns` repeat the first one (the contract still requires the
 * primary type, and the server mirrors it on the page row).
 */
export function draftToCreateInput(draft: TaskPageDraft): CreateTaskPageInput {
  const taskTypes = draft.taskTypes.map(toTaskTypeWrite);
  const primary = taskTypes[0];
  return {
    name: draft.name.trim(),
    description: draft.description.trim() || null,
    icon: draft.icon,
    taskTypeId: primary?.taskTypeId ?? '',
    navGroup: draft.navGroup,
    pageSectionId: draft.pageSectionId,
    sortOrder: parseSortOrder(draft.sortOrder) ?? 0,
    audienceUserGroupIds: draft.audienceUserGroupIds,
    defaultColumns: primary?.defaultColumns ?? null,
    taskTypes,
    allowUserColumnCustomization: draft.allowUserColumnCustomization,
  };
}

export function draftToUpdateInput(
  draft: TaskPageDraft,
): UpdateTaskPageInput | null {
  if (!draft.id) return null;
  return { id: draft.id, ...draftToCreateInput(draft) };
}

/** Server error message when the mutation surfaced one, else the fallback. */
export function mutationErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}
